import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthSessionContext } from "../routing/auth-session-store";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { DialogChat } from "./DialogChat";

export function FloatingChatToggle() {
	const { session } = useAuthSessionContext();
	const navigate = useNavigate();
	const location = useLocation();
	const [chatOpen, setChatOpen] = useState(false);
	const [unreadCount, setUnreadCount] = useState(0);
	const [isMobileViewport, setIsMobileViewport] = useState(false);
	const audioContextRef = useRef<AudioContext | null>(null);

	const playIncomingMessageSound = useCallback(() => {
		if (typeof window === "undefined") return;
		const audioWindow = window as unknown as {
			AudioContext?: typeof AudioContext;
			webkitAudioContext?: typeof AudioContext;
		};
		const AudioContextCtor =
			audioWindow.AudioContext ?? audioWindow.webkitAudioContext;
		if (!AudioContextCtor) return;

		const context = audioContextRef.current ?? new AudioContextCtor();
		if (!audioContextRef.current) {
			audioContextRef.current = context;
		}
		if (context.state === "suspended") {
			void context.resume().catch(() => undefined);
		}

		const startAt = context.currentTime;
		const oscillator = context.createOscillator();
		const gainNode = context.createGain();

		oscillator.type = "sine";
		oscillator.frequency.setValueAtTime(880, startAt);
		oscillator.frequency.exponentialRampToValueAtTime(660, startAt + 0.14);

		gainNode.gain.setValueAtTime(0.0001, startAt);
		gainNode.gain.exponentialRampToValueAtTime(0.18, startAt + 0.015);
		gainNode.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.24);

		oscillator.connect(gainNode);
		gainNode.connect(context.destination);

		oscillator.start(startAt);
		oscillator.stop(startAt + 0.24);
	}, []);

	useEffect(() => {
		return () => {
			if (audioContextRef.current) {
				void audioContextRef.current.close().catch(() => undefined);
				audioContextRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		const updateViewport = () => {
			setIsMobileViewport(window.innerWidth < 640);
		};

		updateViewport();
		window.addEventListener("resize", updateViewport);
		return () => window.removeEventListener("resize", updateViewport);
	}, []);

	const isLogged = Boolean(session?.user?.id);

	if (!isLogged) return null;
	if (location.pathname === "/chat-expanded") return null;

	const userType = session?.user?.type_user;
	if (userType === "Gerente" || userType === "Soporte") {
		return null;
	}

	const isProvider = userType === "Proveedor";

	return (
		<>
			<DialogChat
				open={chatOpen}
				onClose={() => setChatOpen(false)}
				isProviderView={isProvider}
				onUnreadCountChange={setUnreadCount}
				onIncomingMessage={playIncomingMessageSound}
			/>
			<div className='fixed bottom-6 right-6 z-50'>
				<button
					type='button'
					onClick={() => {
						if (isMobileViewport) {
							setChatOpen(false);
							navigate("/chat-expanded");
							return;
						}

						setChatOpen((prev) => !prev);
					}}
					aria-label={`Abrir chat${unreadCount > 0 ? `, ${unreadCount} mensajes sin leer` : ""}`}
					className='group relative inline-flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-primary text-white shadow-lg transition-all duration-200 hover:bg-primary-hover active:scale-95'
				>
					<MessageCircle className='h-6 w-6' />
					{unreadCount > 0 ? (
						<span className='absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white shadow'>
							{unreadCount > 99 ? "99+" : unreadCount}
						</span>
					) : null}
				</button>
			</div>
		</>
	);
}
